/**
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2022 Karmabunny
 */

import { api, Query } from "./base";

export interface ConstraintJson {
    [key: string]: {
        type: string;
        required: Record<string, any>;
    }
}

export interface RequirementJson {
    id: number;
    name: string;
    amount: number;
    images: string[];
    longDescription: string;
}

export interface QuoteJson {
    base: number;
    package: number;
    extras: number;
    requirements: number;
    total: number;
    discount: number;
    tax: number;
    deposit: number;
    payable: number;
    discountId?: number;
    rateTypeId?: number;
}

export interface AccommodationJson {
    categoryId: number;
    entryId: number;
    entryUri: string;
    title: string;
    fromPrice: string;
    sleeps: number;
    requirements: RequirementJson[];
}

export interface SearchResultJson extends AccommodationJson {
    available?: boolean | ConstraintJson;
    rateTypeId?: number;
    constraintMessage?: [string, any];
}

export interface AvailabilityJson extends Array<'depart' | 'disabled'> {}


export interface OptionJson {
    value: string;
    text: string;
}

export interface TermsJson {
    url?: string;
    iframe?: boolean;
    body?: string;
}


/**
 * Asserts that an object is a SearchResultJson type.
 */
 export function isSearchJson(test: any): test is SearchResultJson {
    return (
        typeof test === 'object'
        && test.categoryId
        && test.available
        && test.rates
        && test.availableAreaIds
    );
}


/**
 * Get a item - category + whatever entry fields.
 *
 * @param categoryId - RMS category ID
 */
export async function getItem(categoryId: number | string) {
    const res = await api({
        method: "GET",
        endpoint: "/book/api/item.json",
        query: { categoryId },
    });

    return res.item as AccommodationJson;
}


/**
 * Do a search on a single category.
 *
 * @param options
 */
export async function getSearch(options: Query) {
    const res = await api({
        method: "POST",
        endpoint: "/book/api/search.json",
        body: options,
    });

    return res.item as SearchResultJson;
}


/**
 * Do a search on a single category.
 *
 * @param options
 */
export async function getQuote(options: Query, available = false) {
    const res = await api({
        method: "POST",
        endpoint: "/book/api/quote.json",
        body: options,
        query: {
            available: available ? 1 : 0,
        },
    });

    return res.quote as QuoteJson;
}


/**
 * Get an availability calendar.
 *
 * @param options
 * @returns
 */
export async function getAvailability(options: Query) {
    const res = await api({
        method: "POST",
        endpoint: "/book/api/availability.json",
        body: options,
    });

    return res.availability as AvailabilityJson;
}


/**
 * Get a list of country names.
 */
export async function getCountryNames()
{
    const res = await api({
        method: "GET",
        endpoint: '/book/api/countries.json',
    });
    return res.countries as OptionJson[];
}


/**
 * Get a list of country names.
 */
export async function getBookingSources()
{
    const res = await api({
        method: "GET",
        endpoint: '/book/api/sources.json',
    });
    return res.sources as OptionJson[];
}

/**
 * Terms and conditions.
 */
 export async function getTermsConditions()
 {
     const res = await api({
         method: "GET",
         endpoint: '/book/api/terms.json',
     });
     return res as TermsJson;
 }
